/**
 * MUI / Salify shop UI theme ids for `layout.theme.name` (DB / CMS).
 * Colors and typography overrides live in `layout.theme.mui` (see mock/site-builder-layout-theme-*.json).
 */
export const SALIFY_UI_THEME_IDS = [
  "DEFAULT",
  "GIFT",
  "HEALTH",
  "GROCERY",
  "FURNITURE",
  "FOOTMART",
  "BAZZAR",
  "NOVAPRESS",
  "HAIANH",
  "LUXE",
  "DATHACONS",
] as const;

export type SalifyUiThemeId = (typeof SALIFY_UI_THEME_IDS)[number];

const THEME_ID_SET = new Set<string>(SALIFY_UI_THEME_IDS);

function normalizeThemeId(raw: string): SalifyUiThemeId | undefined {
  const u = raw.trim().toUpperCase();
  if (THEME_ID_SET.has(u)) return u as SalifyUiThemeId;
  return undefined;
}

function getRawLayoutTheme(global: unknown): unknown {
  if (global == null || typeof global !== "object") return undefined;
  const g = global as Record<string, unknown>;
  const layout = g.layout;
  const fromLayout =
    layout != null && typeof layout === "object"
      ? (layout as Record<string, unknown>).theme
      : undefined;
  return fromLayout ?? g.theme;
}

function parseThemeIdFromRaw(raw: unknown): SalifyUiThemeId | undefined {
  if (raw == null) return undefined;
  if (typeof raw === "string") return normalizeThemeId(raw);
  if (typeof raw === "object" && raw !== null) {
    const o = raw as Record<string, unknown>;
    for (const k of ["name", "id", "code", "key"] as const) {
      const v = o[k];
      if (typeof v === "string" && v.trim() !== "") return normalizeThemeId(v);
    }
  }
  return undefined;
}

/** Deep-merge into MUI `createTheme` options. Uses `mui` / `themeOptions` if set, else `palette` / `colors`. */
export function extractMuiThemeOverride(raw: unknown): Record<string, unknown> | undefined {
  if (raw == null || typeof raw !== "object") return undefined;
  const o = raw as Record<string, unknown>;
  const direct = o.mui ?? o.themeOptions ?? o.theme_options;
  if (direct != null && typeof direct === "object") {
    return { ...(direct as Record<string, unknown>) };
  }
  const palette = o.palette ?? o.colors;
  if (palette != null && typeof palette === "object") {
    return { palette: palette as Record<string, unknown> };
  }
  return undefined;
}

/** Reads theme id from `global.layout.theme` or `global.theme`. */
export function parseThemeFromSiteBuilderLayout(global: unknown): SalifyUiThemeId | undefined {
  return parseThemeIdFromRaw(getRawLayoutTheme(global));
}

export type SiteBuilderResolvedTheme = {
  id: SalifyUiThemeId;
  /** Partial MUI theme options merged on top of the base preset for `id`. */
  muiThemeOverride?: Record<string, unknown>;
};

/** Base preset from `layout.theme` + optional color overrides from the same object. */
export function resolveSiteBuilderTheme(global: unknown): SiteBuilderResolvedTheme {
  const raw = getRawLayoutTheme(global);
  const fromLayout = parseThemeIdFromRaw(raw);
  const muiThemeOverride = extractMuiThemeOverride(raw);
  return {
    id: fromLayout ?? "DEFAULT",
    muiThemeOverride,
  };
}

export function resolveSalifyUiTheme(global: unknown): SalifyUiThemeId {
  return resolveSiteBuilderTheme(global).id;
}
